import { useEffect, useRef, type RefObject } from 'react';

/**
 * 菜单外部关闭：捕获阶段监听 pointerdown + Escape / 滚轮。
 *
 * 为什么不用 mousedown：画布空白区在 pointerdown 上调用 preventDefault（平移/框选），
 * 按指针事件规范这会抑制本次交互的 mousedown 兼容事件，依赖 mousedown 的菜单
 * 在"点击空白处"时永远收不到关闭信号（菜单不消失的根因）。
 *
 * 为什么捕获阶段：节点卡片会在 pointerdown 中 stopPropagation 截断冒泡，
 * document 捕获监听先于一切目标处理执行，不受传播拦截影响。
 *
 * onClose 存 ref：注册只依赖 enabled，父组件传入内联箭头函数时不会反复解绑重绑。
 */
export function useMenuExternalDismiss(
  menuRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  enabled = true
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!enabled) return;
    const dismiss = () => onCloseRef.current();
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        dismiss();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    // 画布缩放（wheel）后菜单锚点失真，直接收起（与 NodeParamsPopover 同口径）
    const onWheel = () => dismiss();
    // 延迟绑定，跳过打开菜单的同一指针手势
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('keydown', onKey);
      document.addEventListener('wheel', onWheel, { capture: true, passive: true });
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('wheel', onWheel, { capture: true } as EventListenerOptions);
    };
  }, [menuRef, enabled]);
}
